import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import config from "../config";
import { DAPP_NAME, TransactionName, DAPP_ICON } from "../constants";
import ConfirmModal from "./ConfirmPopup";
import {
  ExecuteReadTransaction,
  ExecuteSubmitTransaction,
  formattedErrorMessage,
} from "../utils";
import MyVerticallyCenteredModal from "./MyVerticallyCenteredModal";
import BigNumber from "bignumber.js";

const getErrorMessage = (message) => {
  try {
    if (message.startsWith("Error:")) {
      message = message.replace("Error: ", "");
    }
    if (message.startsWith("{")) {
      const parsedError = JSON.parse(message);
      if (parsedError?.message?.error === "insufficient balance") {
        return "Not Authorized!";
      }
      return parsedError.message?.error || "Unknown Error";
    }
    if (message === "insufficient balance") {
      return "Not Authorized!";
    }
    if (message === "null") {
      return "Not Authorized!";
    }
    return message;
  } catch (err) {
    return message || "An unexpected error occurred.";
  }
};

const Mint = ({ balance, address, setBalance, isConnected }) => {
  const [totalSupply, setTotalSupply] = useState("");
  const [amount, setAmount] = useState(new BigNumber(0));
  const [responseMessage, setResponseMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [mintError, setMintError] = useState(""); // State for error message
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isConnected) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [isConnected]);

  console.log(typeof amount);
  // To track if the component is still mounted
  const isMountedRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleMint = async () => {
    console.log("Minting...");
    setConfirmPopupOpen(false);
    setMintError("");

    if (new BigNumber(amount).isEqualTo(0)) {
      // Set error message if amount is 0
      setMintError("No amount is set to mint.");
      return;
    }
    try {
      console.log("Address:", address);
      const [res, errMsg] = await ExecuteSubmitTransaction(
        config.CHANNEL_NAME,
        config.CHAINCODE_NAME,
        TransactionName.MINT,
        [address, new BigNumber(amount).multipliedBy(100).toFixed(0)]
      );
      if (res) {
        if (res) {
          fetchTotalSupply();
          setResponseMessage(`${res}`);
          if (totalSupply) {
            setTotalSupply(new BigNumber(totalSupply?.response).plus(amount));
          }
          setBalance(new BigNumber(balance).plus(amount));
          setAmount(new BigNumber(0));
        } else if (res?.error) {
          setResponseMessage(`Error: ${formattedErrorMessage(res)}`);
        }
      } else {
        setResponseMessage(`Error: ${formattedErrorMessage(errMsg)}`);
      }
    } catch (error) {
      console.error("Error minting:", error);
    } finally {
      console.log("Transaction is not in progress anymore");
      setModalShow(true);
    }
  };

  const fetchTotalSupply = async () => {
    try {
      let timeoutInMs = 3000; // 3 sec timeout
      if (balance) {
        timeoutInMs = 0;
      }
      setTimeout(async () => {
        const [totalSupply, errMsg] = await ExecuteReadTransaction(
          config.CHANNEL_NAME,
          config.CHAINCODE_NAME,
          TransactionName.TOTAL_SUPPLY, // Ensure the correct transaction name
          [] // Empty array, no parameters needed for total supply
        );
        console.log("Total supply response: ", totalSupply);
        if (totalSupply) {
          if (isMountedRef.current) {
            setTotalSupply(new BigNumber(totalSupply?.response).dividedBy(100));
          }
        } else {
          throw new Error(formattedErrorMessage(errMsg));
        }
      }, timeoutInMs);
    } catch (error) {
      console.error("Error fetching total supply:", error);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "20px",
        textAlign: "center",
        backgroundColor: "#f2f4f6", // Match the background of your design
        borderRadius: "10px",
      }}
    >
      {/* Main Box aligned with the width of the Mint button */}
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
          borderRadius: "10px",
          width: "100%", // This ensures it will align with the button width
          maxWidth: "600px", // Ensure it's aligned with the button width
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#7A7A7A", marginBottom: "10px" }}
        >
          Mint
        </Typography>

        {/* Toggle between Typography and input */}
        {isEditing ? (
          <input
            value={amount}
            type="text"
            onChange={(e) => {
              const regex = /^\d{0,20}(\.\d{0,2})?$/;
              const value = e.target.value
                .replace(/[^0-9.]/g, "") // Remove non-numeric characters except "."
                .replace(/(\..*?)\..*/g, "$1") // Allow only one decimal point
                .replace(/^(\d+\.\d{2})\d+$/, "$1") // Limit to 2 decimal places
                .replace(/^0([0-9]+)/, "$1"); // Remove leading zeros

              if (regex.test(value)) {
                setAmount(value);
              }
            }}
            onBlur={() => {
              if (amount === "") {
                setAmount(new BigNumber(0).toFixed());
              } else {
                setAmount(new BigNumber(amount).toFixed());
              }
              setIsEditing(false);
            }}
            autoFocus
            style={{
              fontWeight: "bold",
              fontSize: "32px",
              textAlign: "center",
              marginBottom: "10px",
              color: "#9E9E9E",
              border: "none",
              outline: "none",
              appearance: "textfield", // Removes the number input arrows
            }}
          />
        ) : (
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              marginBottom: "10px",
              color: "black",
              cursor: "pointer",
              whiteSpace: "nowrap", // Prevent text wrapping
              overflow: "hidden", // Hide overflowing content
              textOverflow: "ellipsis", // Add ellipsis if content overflows
              fontSize: "clamp(12px, 5vw, 36px)", // Dynamically scale font size based on viewport and container size
              textAlign: "center", // Align text to the center
              maxWidth: "100%", // Ensure it doesn't exceed its container width
            }}
            onClick={() => setIsEditing(true)} // Enter edit mode on click
          >
            ₹{new BigNumber(amount).toFormat(2)}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "10px",
          }}
        >
          <img
            src={DAPP_ICON} // Replace with actual CBDC image
            alt="CBDC Icon"
            style={{ width: "20px", height: "20px", marginRight: "5px" }}
          />
          <Typography variant="body1" sx={{ color: "#7A7A7A" }}>
            CBDC
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            marginBottom: "20px",
            color: "black",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          Total Supply:{" "}
          {isConnected && totalSupply ? (
            <span style={{ marginLeft: "5px", color: "#4C2CF5" }}>
              ₹{totalSupply.toFormat(2)}
            </span>
          ) : (
            <button
              onClick={fetchTotalSupply}
              disabled={!isConnected}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginLeft: "5px",
                color: !isConnected ? "" : "#1976D2",
                textDecoration: "none",
                cursor: !isConnected ? "" : "pointer",
                fontWeight: "bold",
              }}
            >
              Check Now
            </button>
          )}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <Button
            variant="outlined"
            disabled={!isConnected}
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              marginRight: "10px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(100)}
          >
            ₹100
          </Button>
          <Button
            variant="outlined"
            disabled={!isConnected}
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              marginRight: "10px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(250)}
          >
            ₹250
          </Button>
          <Button
            variant="outlined"
            disabled={!isConnected}
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(500)}
          >
            ₹500
          </Button>
        </Box>
      </Box>

      {isConnected && (
        <Box
          sx={{
            marginTop: "20px",
            display: "flex",
            gap: "4px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">Balance:</Typography>
          <Typography
            variant="body2"
            sx={{ color: "#0033cc", fontWeight: "bold" }}
          >
            {loading
              ? "Loading"
              : !loading &&
                isConnected &&
                !isNaN(Number(balance)) &&
                Number(balance) > 0
              ? `₹${new BigNumber(balance).toFormat(2)}`
              : `₹0.00`}
          </Typography>
        </Box>
      )}

      {mintError && ( // Display error message
        <Typography variant="body2" color="error" sx={{ marginTop: "10px" }}>
          {mintError}
        </Typography>
      )}

      {/* Mint button below the box */}
      <Button
        variant="contained"
        fullWidth
        disabled={
          Number(amount) === 0 ||
          new BigNumber(amount).isGreaterThan(new BigNumber(balance)) ||
          !isConnected
        }
        onClick={() => {
          setConfirmPopupOpen(true);
        }}
        sx={{
          backgroundColor: "#0055FF",
          borderRadius: "8px",
          height: "45px",
          maxWidth: "600px", // Ensure this width matches the box width
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "bold",
          marginTop: "20px",
          "&:hover": {
            backgroundColor: "#0044CC",
          },
        }}
      >
        Mint
      </Button>
      <ConfirmModal
        open={confirmPopupOpen}
        setOpen={setConfirmPopupOpen}
        onConfirm={handleMint}
      />
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />
    </Box>
  );
};

export default Mint;
